/**
 * Public "Request a demo" page (`/request-demo`).
 *
 * 7 steps — 1:1 with the mobile RequestDemoScreen flow:
 *   1. Your role
 *   2. Teaching area
 *   3. Typical class size
 *   4. Main interest
 *   5. Main goal
 *   6. Contact details        (first name, last name, university, email, phone)
 *   7. Pilot program
 *
 * Preferred demo times are no longer collected in-app; we schedule by email.
 *
 * No auth required: this is the marketing-funnel form for professors
 * who don't have a Microlab account yet. Submits to the public backend
 * endpoint POST /api/demo-request/public.
 */

import { useCallback, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Box from "@mui/material/Box";
import Container from "@mui/material/Container";
import Stack from "@mui/material/Stack";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import TextField from "@mui/material/TextField";
import CircularProgress from "@mui/material/CircularProgress";
import Snackbar from "@mui/material/Snackbar";
import Alert from "@mui/material/Alert";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import HomeRoundedIcon from "@mui/icons-material/HomeRounded";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";

import { SubmitPublicDemoRequestCall } from "../../redux/api/demoRequest.api";
import DemoStepDots from "./DemoStepDots";
import DemoFlowHeaderCard from "./DemoFlowHeaderCard";
import DemoOptionButton from "./DemoOptionButton";
import {
  COPY,
  DEMO_CLASS_SIZE_OPTIONS,
  DEMO_MAIN_GOAL_OPTIONS,
  DEMO_MAIN_INTEREST_OPTIONS,
  DEMO_PILOT_OPTIONS,
  DEMO_ROLE_OPTIONS,
  DEMO_TEACHING_AREA_OPTIONS,
  TOTAL_STEPS,
} from "./demoRequestConstants";

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function isValidEmail(s) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(s || "").trim());
}

function isAppleRelayEmail(s) {
  return String(s || "")
    .trim()
    .toLowerCase()
    .endsWith("@privaterelay.appleid.com");
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export default function DemoRequestPage() {
  const theme = useTheme();
  const isMdUp = useMediaQuery(theme.breakpoints.up("md"));
  const navigate = useNavigate();

  const [step, setStep] = useState(1);
  const [done, setDone] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [toast, setToast] = useState(null); // { msg, severity }

  const [form, setForm] = useState({
    firstName: "",
    lastName: "",
    universityName: "",
    role: "Professor",
    teachingArea: "Histology",
    classSize: null,
    mainInterest: null,
    mainGoal: null,
    email: "",
    phone: "",
    pilot: null,
  });

  const setField = useCallback((key, value) => {
    setForm((f) => ({ ...f, [key]: value }));
  }, []);

  const showToast = useCallback((msg, severity = "warning") => {
    setToast({ msg, severity });
  }, []);

  // -------------------------------------------------------------------------
  // Step navigation
  // -------------------------------------------------------------------------

  const goPrev = useCallback(() => {
    if (done) {
      navigate("/");
      return;
    }
    setStep((s) => Math.max(1, s - 1));
  }, [done, navigate]);

  const goNext = useCallback(() => {
    setStep((s) => Math.min(TOTAL_STEPS, s + 1));
  }, []);

  // -------------------------------------------------------------------------
  // Submit
  // -------------------------------------------------------------------------

  const submit = useCallback(async () => {
    if (!form.firstName.trim()) {
      showToast("Please enter your first name.", "warning");
      setStep(6);
      return;
    }
    if (!isValidEmail(form.email)) {
      showToast(COPY.invalidEmail, "warning");
      setStep(6);
      return;
    }
    if (isAppleRelayEmail(form.email)) {
      showToast(COPY.appleRelayBlocked, "warning");
      setStep(6);
      return;
    }
    if (form.pilot == null) {
      showToast(COPY.pickPilotOption, "warning");
      return;
    }

    setSubmitting(true);
    try {
      const body = {
        firstName: form.firstName.trim(),
        lastName: form.lastName.trim(),
        universityName: form.universityName.trim() || undefined,
        role: form.role,
        teachingArea: form.teachingArea,
        classSize: form.classSize,
        mainInterest: form.mainInterest,
        mainGoal: form.mainGoal,
        demoDates: [],
        timePreference: "",
        email: form.email.trim(),
        phone: form.phone.trim() || undefined,
        pilot: form.pilot,
      };

      const res = await SubmitPublicDemoRequestCall(body);
      const st = typeof res?.status === "number" ? res.status : null;
      if (st != null && st >= 400) {
        showToast(res?.message || COPY.submitError, "error");
        return;
      }
      setDone(true);
    } catch (err) {
      showToast(err?.message || COPY.submitError, "error");
    } finally {
      setSubmitting(false);
    }
  }, [form, showToast]);

  // -------------------------------------------------------------------------
  // Step renderers
  // -------------------------------------------------------------------------

  const renderOptionList = (options, valueKey) =>
    options.map((o) => (
      <DemoOptionButton
        key={o.value}
        label={o.label}
        sub={o.hint}
        selected={form[valueKey] === o.value}
        onPress={() => {
          setField(valueKey, o.value);
        }}
      />
    ));

  const renderStep = () => {
    if (done) {
      return (
        <Stack spacing={2.5} sx={{ py: 3, alignItems: "center", textAlign: "center" }}>
          <CheckCircleRoundedIcon
            sx={{ fontSize: 64, color: "primary.main" }}
          />
          <Typography variant="h4" sx={{ fontWeight: 800, color: "primary.main" }}>
            {COPY.successTitle}
          </Typography>
          <Typography
            sx={{ color: "text.secondary", maxWidth: 480, lineHeight: 1.6 }}
          >
            {COPY.successBody}
          </Typography>
          <Button
            component={Link}
            to="/"
            variant="contained"
            startIcon={<HomeRoundedIcon />}
            sx={{ mt: 1, color: "#fff" }}
          >
            Back to Microlab
          </Button>
        </Stack>
      );
    }

    switch (step) {
      case 1:
        return (
          <Stack>
            {renderOptionList(DEMO_ROLE_OPTIONS, "role")}
            <Button
              fullWidth
              variant="contained"
              size="large"
              onClick={goNext}
              sx={{ mt: 1, color: "#fff" }}
            >
              {`Continue with "${form.role}"`}
            </Button>
          </Stack>
        );

      case 2:
        return (
          <Stack>
            {renderOptionList(DEMO_TEACHING_AREA_OPTIONS, "teachingArea")}
            <Button
              fullWidth
              variant="contained"
              size="large"
              onClick={goNext}
              sx={{ mt: 1, color: "#fff" }}
            >
              {`Continue with "${form.teachingArea}"`}
            </Button>
          </Stack>
        );

      case 3:
        return (
          <Stack>
            {renderOptionList(DEMO_CLASS_SIZE_OPTIONS, "classSize")}
            {form.classSize != null && (
              <Button
                fullWidth
                variant="contained"
                size="large"
                onClick={goNext}
                sx={{ mt: 1, color: "#fff" }}
              >
                {COPY.continue}
              </Button>
            )}
          </Stack>
        );

      case 4:
        return (
          <Stack>
            {renderOptionList(DEMO_MAIN_INTEREST_OPTIONS, "mainInterest")}
            {form.mainInterest != null && (
              <Button
                fullWidth
                variant="contained"
                size="large"
                onClick={goNext}
                sx={{ mt: 1, color: "#fff" }}
              >
                {COPY.continue}
              </Button>
            )}
          </Stack>
        );

      case 5:
        return (
          <Stack>
            {renderOptionList(DEMO_MAIN_GOAL_OPTIONS, "mainGoal")}
            {form.mainGoal != null && (
              <Button
                fullWidth
                variant="contained"
                size="large"
                onClick={goNext}
                sx={{ mt: 1, color: "#fff" }}
              >
                {COPY.continue}
              </Button>
            )}
          </Stack>
        );

      case 6:
        return (
          <Stack spacing={2}>
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={2}
              sx={{ width: "100%" }}
            >
              <TextField
                fullWidth
                label={COPY.firstName}
                value={form.firstName}
                onChange={(e) => setField("firstName", e.target.value)}
                autoFocus
                sx={{ flex: 1 }}
              />
              <TextField
                fullWidth
                label={COPY.lastName}
                value={form.lastName}
                onChange={(e) => setField("lastName", e.target.value)}
                sx={{ flex: 1 }}
              />
            </Stack>
            <TextField
              fullWidth
              label={COPY.universityOptional}
              value={form.universityName}
              onChange={(e) => setField("universityName", e.target.value)}
            />
            <TextField
              fullWidth
              type="email"
              label={COPY.workEmail}
              value={form.email}
              onChange={(e) => setField("email", e.target.value)}
            />
            <TextField
              fullWidth
              label={COPY.phoneOptional}
              value={form.phone}
              onChange={(e) => setField("phone", e.target.value)}
            />
            <Button
              fullWidth
              variant="contained"
              size="large"
              sx={{ color: "#fff" }}
              onClick={() => {
                if (!form.firstName.trim()) {
                  showToast("Please enter your first name.", "warning");
                  return;
                }
                if (!isValidEmail(form.email)) {
                  showToast(COPY.invalidEmail, "warning");
                  return;
                }
                if (isAppleRelayEmail(form.email)) {
                  showToast(COPY.appleRelayBlocked, "warning");
                  return;
                }
                goNext();
              }}
            >
              {COPY.continue}
            </Button>
          </Stack>
        );

      case 7:
        return (
          <Stack>
            {submitting ? (
              <Box sx={{ py: 4, display: "flex", justifyContent: "center" }}>
                <CircularProgress />
              </Box>
            ) : (
              <>
                {renderOptionList(DEMO_PILOT_OPTIONS, "pilot")}
                {form.pilot != null && (
                  <Button
                    fullWidth
                    variant="contained"
                    size="large"
                    onClick={submit}
                    sx={{ mt: 1, color: "#fff" }}
                  >
                    {COPY.finish}
                  </Button>
                )}
              </>
            )}
          </Stack>
        );

      default:
        return null;
    }
  };

  // -------------------------------------------------------------------------
  // Layout (desktop = centered card, mobile = full-width column)
  // -------------------------------------------------------------------------

  return (
    <Box
      sx={{
        minHeight: "100vh",
        bgcolor: "background.default",
        pb: 4,
      }}
    >
      {/* Page header */}
      <Box
        sx={{
          borderBottom: "1px solid",
          borderColor: "divider",
          bgcolor: "background.paper",
          position: "sticky",
          top: 0,
          zIndex: 5,
        }}
      >
        <Container maxWidth="md">
          <Stack
            direction="row"
            alignItems="center"
            spacing={1.5}
            sx={{ py: 1.5 }}
          >
            <IconButton onClick={goPrev} aria-label="back" size="small">
              <ArrowBackIcon />
            </IconButton>
            <Typography
              sx={{ flex: 1, fontWeight: 700, fontSize: isMdUp ? 18 : 16 }}
            >
              {COPY.pageTitle}
            </Typography>
            {!done && (
              <Typography
                sx={{ fontSize: 12, color: "text.secondary", fontWeight: 600 }}
              >
                {COPY.stepOf(step, TOTAL_STEPS)}
              </Typography>
            )}
          </Stack>
        </Container>
      </Box>

      <Container maxWidth="md" sx={{ pt: 3 }}>
        <Paper
          elevation={isMdUp ? 1 : 0}
          sx={{
            borderRadius: 3,
            p: { xs: 2, sm: 3, md: 4 },
            border: isMdUp ? "1px solid" : "none",
            borderColor: "divider",
            bgcolor: "background.paper",
          }}
        >
          {!done && (
            <>
              <DemoStepDots total={TOTAL_STEPS} current={step} />
              <DemoFlowHeaderCard step={step} />
            </>
          )}
          {renderStep()}
        </Paper>
      </Container>


      <Snackbar
        open={!!toast}
        autoHideDuration={4500}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        {toast && (
          <Alert
            severity={toast.severity || "warning"}
            onClose={() => setToast(null)}
            variant="filled"
          >
            {toast.msg}
          </Alert>
        )}
      </Snackbar>
    </Box>
  );
}
